import { IPromise } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { StateService } from "@uirouter/angularjs";
import { ReactionService } from "@gtmhub/comments/services/reaction.service";
import { ProgressColoring } from "@gtmhub/configuration/services/progress-coloring";
import { IStateInit } from "@gtmhub/core/routing";
import dayjs from "@gtmhub/dayjs";
import { IIndicator } from "@gtmhub/error-handling";
import { GoalService } from "@gtmhub/goals/services/goal.service";
import { IInsight } from "@gtmhub/insights";
import { MetricService } from "@gtmhub/okrs/metrics/services/metric.service";
import { IMetricSnapshot, Metric } from "@webapp/okrs/metrics/models/metric.models";
import { IComment } from "../models/comments";
import { IAggregatedReaction, IReaction } from "../models/reactions";
import { CommentService } from "../services/comment.service";

interface IThreadScope extends IModalScope {
  params: IThreadStateParams;
  metric: Metric;
  metadata: {
    metricId: string;
  };
  u: IMetricSnapshot;
  insight: IInsight;
  indicators: {
    getItem?: IIndicator;
  };
  snapshotReactions: IAggregatedReaction[];
  commentsCount: number;
  commentTargetTitle: string;
  commentMentions: string[];
}

interface IThreadStateParams {
  targetId: string;
  targetType: string;
  insightName: string;
}

export class ThreadCtrl implements IStateInit {
  public static $inject = ["$scope", "$stateParams", "$q", "MetricService", "GoalService", "ReactionService", "CommentService", "ProgressColoring", "$state"];

  constructor(
    private $scope: IThreadScope,
    private $stateParams: IThreadStateParams,
    private $q: ng.IQService,
    private metricService: MetricService,
    private goalService: GoalService,
    private reactionService: ReactionService,
    private commentService: CommentService,
    private progressColoring: ProgressColoring,
    private $state: StateService
  ) {
    this.$scope.indicators = {};
    this.$scope.params = { ...this.$stateParams };

    this.$scope.$on("commentPosted", this.onCommentPosted);
    this.$scope.$on("commentDeleted", this.onCommentDeleted);

    this.$scope.$on("reactionCreated", this.onReactionCreated);
    this.$scope.$on("reactionDeleted", this.onReactionDeleted);
  }

  stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init = (): IPromise<unknown> => {
    if (this.$stateParams.targetType === "checkin") {
      return this.getMetric();
    }
  };

  private getMetric = () => {
    this.$scope.indicators.getItem = { progress: true };

    const promiseMap = {
      metric: this.metricService.getMetricByCheckin(this.$stateParams.targetId),
      reactions: this.reactionService.getReactions({ targetIds: this.$stateParams.targetId }),
      // add fields to comments
      comments: this.commentService.getComments({ targetType: "checkin", targetIds: this.$stateParams.targetId }),
    };

    return this.$q
      .all(promiseMap)
      .then((resolvedPromiseMap) => {
        this.$scope.metric = resolvedPromiseMap.metric;
        this.$scope.metadata = {
          metricId: this.$scope.metric.id,
        };
        this.$scope.commentTargetTitle = this.$scope.metric.name;

        for (let i = 0; i < this.$scope.metric.metricHistory.length; i++) {
          if (this.$scope.metric.metricHistory[i].id && this.$scope.metric.metricHistory[i].id === this.$stateParams.targetId) {
            this.$scope.u = this.$scope.metric.metricHistory[i];

            if (dayjs(this.$scope.metric.metricHistory[i].updatedAt).format("YYYY-MM-DD") !== dayjs(this.$scope.metric.metricHistory[i].createdAt).format("YYYY-MM-DD")) {
              this.$scope.u.updatedLate = true;
            }

            this.$scope.u.view = {
              color: this.progressColoring.getColorOrGeneric(this.$scope.metric.metricHistory[i].currentAttainment),
            };
          }
        }

        this.$scope.snapshotReactions = resolvedPromiseMap.reactions.items;
        this.$scope.commentsCount = resolvedPromiseMap.comments.totalCount;

        return this.goalService.getGoal(this.$scope.metric.goalId).then((goal) => {
          const mentions = Array.isArray(this.$scope.u?.mentions?.mentioned?.comment) ? this.$scope.u.mentions.mentioned.comment : [];
          this.$scope.commentMentions = [...this.$scope.metric.ownerIds, ...goal.ownerIds, ...mentions];
        });
      })
      .then(
        () => {
          delete this.$scope.indicators.getItem;
        },
        (error) => (this.$scope.indicators.getItem = { error })
      );
  };

  private onCommentPosted = (event: ng.IAngularEvent, comment: IComment): void => {
    if (this.$stateParams.targetId === comment.targetId) {
      this.$scope.commentsCount++;
    }
  };

  private onCommentDeleted = (event: ng.IAngularEvent, comment: IComment): void => {
    if (this.$stateParams.targetId === comment.targetId && this.$scope.commentsCount > 0) {
      this.$scope.commentsCount--;
    }
    this.$state.go("^.^");
  };

  private onReactionCreated = (event: ng.IAngularEvent, newReaction: IReaction): void => {
    if (newReaction.targetId === this.$stateParams.targetId) {
      this.$scope.snapshotReactions = this.reactionService.onReactionCreated(this.$scope.snapshotReactions, newReaction);
    }
  };

  private onReactionDeleted = (event: ng.IAngularEvent, deletedReaction: IAggregatedReaction): void => {
    if (deletedReaction.targetId === this.$stateParams.targetId) {
      this.$scope.snapshotReactions = this.reactionService.onReactionDeleted(this.$scope.snapshotReactions, deletedReaction);
    }
  };
}
